/**
 * Zod input schemas: config. Split out of the one big schemas.ts (Oct 2026) so a session opens only the tool it
 * works on; nothing changed in the schemas themselves. Import as before, from '@daaily/shared' (or './schemas.js'
 * inside shared); the barrel re-exports every file in this folder. Shared primitives (EmailSchema, MonthSchema, …)
 * live in primitives.ts; cross-tool references are explicit imports between these files.
 */
import { z } from 'zod';
import { EmailSchema, ShortTextSchema } from './primitives.js';
// ===========================================================================
// CONFIG (admin only)
// ===========================================================================
export const AppConfigSchema = z.object({
    adminEmails: z.array(EmailSchema).min(1, 'At least one admin email is required'),
    /**
     * Hub-level admins. Can provision users to tools but don't automatically
     * have any tool's admin rights. Distinct from `adminEmails` (which is the
     * Expense Tool's admin list).
     */
    hubAdminEmails: z
        .array(EmailSchema)
        .min(1, 'At least one hub admin email is required')
        .max(10, 'No more than 10 hub admin emails'),
    accountingEmails: z
        .array(EmailSchema)
        .min(1, 'At least one accounting email is required')
        .max(10, 'No more than 10 accounting emails'),
    hrEmail: EmailSchema,
    billingEmail: EmailSchema,
    approvalSlaDays: z.number().int().min(1).max(60),
    draftCleanupDays: z.number().int().min(7).max(365),
    /**
     * Day-of-month (1–28) the Payroll Expense Summary email is sent.
     * Default 10 = sent on the 10th covering "10th of prior month → 9th of current".
     * Capped at 28 so February doesn't fail.
     */
    payrollSummaryDay: z.number().int().min(1).max(28),
    hrDriveFolderId: z.string(),
    /**
     * Tile-icon URLs keyed by tool slug. Each value is a public Firebase
     * Storage URL for the uploaded image, or null/missing if no override
     * has been set (in which case the tile uses a built-in illustration).
     *
     * Schema is permissive (Record<string, string | null>) rather than a
     * strict per-slug shape so adding a new tool slug doesn't require a
     * schema migration before icons can be uploaded for it.
     */
    toolIcons: z.record(z.string(), z.string().nullable()).default({}),
    /**
     * Per-slug label overrides. Empty/missing fields fall back to the
     * registry defaults. Caps mirror the registry: name ≤ 60, description
     * ≤ 200 chars. Strings are trimmed; whitespace-only collapses to ''.
     */
    toolLabels: z
        .record(z.string(), z
        .object({
        name: z.string().trim().max(60).optional(),
        description: z.string().trim().max(200).optional(),
    })
        .nullable())
        .default({}),
    /**
     * Credit Card Submissions approver list. Mirrors `accountingEmails`:
     * at least one, capped at 10 for backup coverage. First entry receives
     * the submission notification. Backfilled by migrate-credit-cards-config.
     */
    creditCardApproverEmails: z
        .array(EmailSchema)
        .min(1, 'At least one credit card approver email is required')
        .max(10, 'No more than 10 credit card approver emails'),
    /** Recipient of the approved-credit-card handoff email (stitched PDF). */
    kreditorenEmail: EmailSchema,
    /**
     * Cost centers (Kostenstellen) selectable on user records. Admin-maintained;
     * default empty. Each entry trimmed, 1–60 chars.
     */
    kostenstellen: z.array(z.string().trim().min(1).max(60)).default([]),
    /** Subscriptions → Promotions: code-owner revenue share per Piano promotion (Oct 2026). */
    subscriptionPromotionShares: z.array(z.object({
        promotionId: z.string().trim().min(1).max(64),
        ownerName: z.string().trim().max(120),
        sharePct: z.number().min(0).max(100),
        basis: z.enum(['original', 'paid']),
    })).max(500).nullable().optional(),
    /** Subscriptions → Promotions: code groups with campaign start dates (Oct 2026). */
    subscriptionPromotionGroups: z.array(z.object({
        id: z.enum(['ambassadors', 'winback']),
        name: z.string().trim().min(1).max(60),
        codes: z.array(z.string().trim().min(1).max(80)).max(2000),
        startDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable(),
        codeStartDates: z.record(z.string().regex(/^\d{4}-\d{2}-\d{2}$/)).nullable().optional(),
    })).max(10).nullable().optional(),
    /** Subscriptions → Promotions: codes left out of "Other" by an admin (Oct 2026). */
    subscriptionPromotionExcludes: z.array(z.string().trim().min(1).max(80)).max(2000).nullable().optional(),
    companyName: ShortTextSchema,
    companyAddress: z.array(ShortTextSchema).min(1).max(5),
});
/** Four-eyes config change — propose a new value for a guarded field. */
export const ConfigRequestFolderChangeSchema = z.object({
    field: z.enum(['hrDriveFolderId', 'documentVaultDriveFolderId']),
    newValue: z.string().trim().max(200),
});
/** Approve / reject a pending guarded-config change. */
export const ConfigDecideFolderChangeSchema = z.object({
    requestId: z.string().min(1),
    reason: z.string().trim().max(500).optional(),
});
//# sourceMappingURL=config.js.map